export const PARTNER_NAMES_STORAGE_KEY = 'hc_partner_names';

const MAX_SAVED_PARTNER_NAMES = 10;

const GENERIC_PARTNER_NAMES = new Set(['partner', 'yourself']);

function readPartnerNames(): string[] {
  try {
    const raw = localStorage.getItem(PARTNER_NAMES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (value): value is string =>
        typeof value === 'string' && value.trim().length > 0
    );
  } catch {
    return [];
  }
}

function writePartnerNames(names: string[]): void {
  try {
    localStorage.setItem(PARTNER_NAMES_STORAGE_KEY, JSON.stringify(names));
  } catch {
    /* ignore quota / private mode */
  }
}

export function getSavedPartnerNames(): string[] {
  return readPartnerNames();
}

export function getLastPartnerName(): string | null {
  return getSavedPartnerNames()[0] ?? null;
}

/** Persist a partner display name for setup autocomplete (most recent first). */
export function rememberPartnerName(name: string): void {
  const trimmed = name.trim();
  if (!trimmed || GENERIC_PARTNER_NAMES.has(trimmed.toLowerCase())) {
    return;
  }

  const existing = readPartnerNames();
  const withoutDuplicate = existing.filter(
    existingName => existingName.toLowerCase() !== trimmed.toLowerCase()
  );
  writePartnerNames(
    [trimmed, ...withoutDuplicate].slice(0, MAX_SAVED_PARTNER_NAMES)
  );
}

export const OWN_NAME_STORAGE_KEY = 'hc_own_name';

/** The name the player typed for themselves in onboarding, if any. */
export function getOwnName(): string | null {
  try {
    const value = localStorage.getItem(OWN_NAME_STORAGE_KEY)?.trim();
    return value ? value : null;
  } catch {
    return null;
  }
}

export function rememberOwnName(name: string): void {
  const trimmed = name.trim();
  if (!trimmed) return;
  try {
    localStorage.setItem(OWN_NAME_STORAGE_KEY, trimmed);
  } catch {
    /* ignore quota / private mode */
  }
}

/** "Sara Ahmed" -> "Sara": what the app calls someone by default. */
export function accountFirstName(accountName?: string | null): string {
  return accountName?.trim().split(/\s+/)[0] ?? '';
}

/**
 * Player 1's display name, the one place every setup screen reads it from:
 * the name typed in onboarding, else the first name of the account.
 */
export function resolveOwnName(accountName?: string | null): string {
  return getOwnName() ?? accountFirstName(accountName);
}
